import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ButtonDeleteIds } from 'components/ButtonDeleteIds';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { DeleteItemFrom } from 'components/DeleteItemFrom';
import { TableTopPanel } from 'components/TableTopPanel';
import { AG_GRID_ICON_SETUP } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimActKc2OtherData, BimActKc2OtherItemData } from 'gql/graphql';
import {
    BimActKc2OtherStatusEnum,
    BimChapterEnum,
    ContractStatusEnum,
    CostGroupEnum,
    ProjectStatusEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { type IUseAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { BimWorkOrderPriceRowDetails } from 'pages/Project/BimProject/BimCommonComponents/BimWorkOrderPriceRowDetails';
import { getRowClass } from 'pages/Project/BimProject/BimCommonComponents/WorkOrderPriceCellRenderers';
import { useHighLightRulesWithRequestData } from 'pages/Project/Common/hooks/useHighLightRulesWithRequestData';

import type { RowClickedEvent } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { RefObject } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useBimCreateActKC2OtherCorrection } from '../BimEditActKC2OtherCorrection/hooks/useBimCreateActKC2OtherCorrection';
import { BimWorkOrderOtherActKc2Details } from '../BimWorkOrderOtherActsKc2';
import { useBimWorkOrderTotalsRow } from '../hooks';
import { useBimWorkOrderPriceStates } from '../hooks/useBimWorkOrderPriceStates';
import { BimWorkOrderRowTypeEnum } from '../types';

import { useBimWorkOrderDeletePrices, useWorkOrderOtherColumns, useWorkOrderOtherPriceList } from './hooks';
import { useBimActsKC2Other } from './hooks/useBimActsKC2Other';
import { useBimRemoveActKC2Other } from './hooks/useBimRemoveActKC2Other';

import Style from './BimWorkOrderOthersPrice.module.css';

const defaultColDef = {
    resizable: true,
    wrapHeaderText: true,
};

interface IBimWorkOrderOthersTable {
    workOrderId: number;
    workOrderRootObjectId: number | undefined;
    constructionTypeId?: number;
    totalDetailsRowOpenOuter: boolean;
    hasInProgress: boolean;
    statusWorkOrder: WorkOrderStatusEnum;
    toggleTotalDetailsOuter: () => void;
    onOpenActKc2Edit: (act: BimActKc2OtherItemData | BimActKc2OtherData) => any;
    isFrameContract: boolean;
    refreshProject: () => void;
    resetCheckedRowsRef: React.MutableRefObject<(() => void) | undefined>;
    contractStatus?: ContractStatusEnum;
    orderRules?: IUseAccess['orderBim'];
    statusProject?: ProjectStatusEnum;
}

const fakeRow = { rowType: BimWorkOrderRowTypeEnum.TotalsString, data: null };

const getRowId = (params: any) => params?.data?.id;

export const BimWorkOrderOthersTable = forwardRef((props: IBimWorkOrderOthersTable, ref: any) => {
    const {
        workOrderId,
        workOrderRootObjectId,
        statusWorkOrder,
        constructionTypeId,
        totalDetailsRowOpenOuter,
        toggleTotalDetailsOuter,
        onOpenActKc2Edit,
        isFrameContract,
        refreshProject,
        resetCheckedRowsRef,
        contractStatus,
        orderRules,
        hasInProgress,
        statusProject,
    } = props;

    const { id: projectId } = useParams();

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [actKc2OtherItemToDelete, setActKc2OtherItemToDelete] = useState<BimActKc2OtherItemData>();
    const [onDeleteSuccessExternalCallback, setOnDeleteSuccessExternalCallback] = useState<() => void>();
    const [actKc2OtherForDetails, setActKc2OtherForDetails] = useState<BimActKc2OtherItemData | null>(null);
    const [actKc2OtherForCorrection, setActKc2OtherForCorrection] = useState<BimActKc2OtherItemData | null>(null);

    const disabledByContractStatus = useMemo(
        () => contractStatus === ContractStatusEnum.Delayed || contractStatus === ContractStatusEnum.Completed,
        [contractStatus],
    );

    const onActKc2OtherDetailsClose = useCallback(() => {
        setActKc2OtherForDetails(null);
    }, []);

    const {
        bimWorkOrderPriceStatesData: { hasExcessCompletedOthers },
        bimWorkOrderPriceStatesMakeRequest,
    } = useBimWorkOrderPriceStates(BimChapterEnum.Others, workOrderRootObjectId);

    const {
        tableItems,
        fetchedItemsCounter,
        totalItems,
        refreshWorkOrderOtherPriceList: refreshWorkOrderOtherPrice,
        changeFilterHandler,
        nameFilterItems,
        othersPriceDataStatus,
    } = useWorkOrderOtherPriceList({
        workOrderId,
        workOrderRootObjectId,
    });

    const refreshWorkOrderOtherPriceList = useCallback(() => {
        refreshWorkOrderOtherPrice();
        bimWorkOrderPriceStatesMakeRequest();
    }, [bimWorkOrderPriceStatesMakeRequest, refreshWorkOrderOtherPrice]);

    const reInitFilter = useCallback(() => {
        (ref?.current as RefObject<AgGridReact>)?.current?.api?.destroyFilter?.('name');
    }, [ref]);

    useEffect(() => {
        reInitFilter();
    }, [nameFilterItems.length, reInitFilter, workOrderId, workOrderRootObjectId]);

    const { bimActsKC2OtherData, bimActsKC2OtherMakeRequest } = useBimActsKC2Other(workOrderId, workOrderRootObjectId);

    const onDeleteActSuccess = useCallback(() => {
        refreshWorkOrderOtherPriceList();
        bimActsKC2OtherMakeRequest();
        onDeleteSuccessExternalCallback?.();
        refreshProject();
        setOnDeleteSuccessExternalCallback(undefined);
    }, [bimActsKC2OtherMakeRequest, onDeleteSuccessExternalCallback, refreshProject, refreshWorkOrderOtherPriceList]);

    const onCreateActKc2OtherCorrectionSuccess = useCallback(
        (data?: BimActKc2OtherData) => {
            if (data) {
                onOpenActKc2Edit(data);
            }
        },
        [onOpenActKc2Edit],
    );

    const { bimRemoveActKC2OtherStatus, bimRemoveActKC2OtherMakeRequest } = useBimRemoveActKC2Other(onDeleteActSuccess);

    const bimCreateActKC2OtherCorrection = useBimCreateActKC2OtherCorrection({
        onSuccess: onCreateActKc2OtherCorrectionSuccess,
    });

    const onActionConfirm = useCallback(() => {
        if (actKc2OtherItemToDelete) {
            bimRemoveActKC2OtherMakeRequest(actKc2OtherItemToDelete?.id);
        } else if (actKc2OtherForCorrection) {
            bimCreateActKC2OtherCorrection(actKc2OtherForCorrection?.id);
        }
    }, [
        actKc2OtherForCorrection,
        actKc2OtherItemToDelete,
        bimCreateActKC2OtherCorrection,
        bimRemoveActKC2OtherMakeRequest,
    ]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onActionConfirm,
        });

    const onDeleteActConfirmationClose = useCallback(() => {
        handleCloseConfirmationDialog();

        setActKc2OtherItemToDelete(undefined);
        setOnDeleteSuccessExternalCallback(undefined);
    }, [handleCloseConfirmationDialog]);

    const onCorrectActConfirmationClose = useCallback(() => {
        handleCloseConfirmationDialog();

        setActKc2OtherForCorrection(null);
    }, [handleCloseConfirmationDialog]);

    const menuItemsGetter = useCallback(
        (act: BimActKc2OtherItemData, aktKC2BimOtherRules: IUseAccess['aktKC2BimOther']) => {
            const editItem = {
                label: `${act.isInactive ? 'Просмотреть' : 'Редактировать'} реквизиты`,
                onClick: () => {
                    onOpenActKc2Edit(act);
                },
                groupId: 1,
                disabled: statusProject === ProjectStatusEnum.Delayed || !aktKC2BimOtherRules.canEditActKC2,
            };

            const detailsItem = {
                label: 'Просмотреть итоги Акта',
                onClick: () => {
                    setActKc2OtherForDetails(act);
                },
                groupId: 1,
            };

            const removeItem = {
                label: 'Удалить',
                disabled:
                    statusWorkOrder === WorkOrderStatusEnum.Delayed ||
                    statusProject === ProjectStatusEnum.Delayed ||
                    act.status !== BimActKc2OtherStatusEnum.InProgress ||
                    bimRemoveActKC2OtherStatus === RequestStatus.FETCHING ||
                    disabledByContractStatus ||
                    !aktKC2BimOtherRules.canDelete,
                onClick: () => {
                    handleOpenConfirmationDialog();
                    setActKc2OtherItemToDelete(act);

                    sendGpnAnalyticsEvent({
                        componentId: 'rim_act KC delete button_pressed',
                        projectId,
                        orderId: workOrderId,
                        costGroup: CostGroupEnum.Others,
                        actKC2Status: act?.status,
                        actKC2Id: act?.id,
                        methodology: projectData?.methodology,
                        projectName: projectData?.name,
                        projectDO: projectData?.subsidiary?.name,
                        projectType: projectData?.contractType,
                    });
                },
                groupId: 2,
            };

            const correctionItem = {
                label: 'Создать корректировочный акт',
                groupId: 1,
                disabled:
                    statusWorkOrder === WorkOrderStatusEnum.Delayed ||
                    statusProject === ProjectStatusEnum.Delayed ||
                    act.status !== BimActKc2OtherStatusEnum.Accepted ||
                    !!act.isInactive,
                onClick: () => {
                    handleOpenConfirmationDialog();
                    setActKc2OtherForCorrection(act);
                },
            };

            return [editItem, detailsItem, correctionItem, removeItem];
        },
        [
            statusProject,
            statusWorkOrder,
            bimRemoveActKC2OtherStatus,
            disabledByContractStatus,
            onOpenActKc2Edit,
            handleOpenConfirmationDialog,
            sendGpnAnalyticsEvent,
            projectId,
            workOrderId,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectData?.contractType,
        ],
    );

    const {
        fullSetColumns,
        detailsColumns,
        settingsColumns,
        handleTableSettingsConfirm,
        isOtherColumnVisible,
        checkedRowsArr,
        resetCheckedRows,
        isSingleDeleteModalOpened,
        closeSingleDeleteModalHandler,
        closeTotalDetails,
        totalDetailsOpened,
        outerExpandOpener,
    } = useWorkOrderOtherColumns({
        refreshWorkOrderOtherPriceList,
        refreshBimActsKC2Other: bimActsKC2OtherMakeRequest,
        toggleTotalDetailsOuter,
        totalDetailsRowOpenOuter,
        constructionTypeId,
        statusWorkOrder,
        hasInProgress,
        actsKc2Other: bimActsKC2OtherData?.bimActsKC2Other?.items,
        menuItemsGetter,
        isFrameContract,
        changeFilterHandler,
        nameFilterItems,
        contractStatus,
        hasExcessCompletedOthers,
        orderRules,
        statusProject,
        othersPriceDataStatus,
    });

    useImperativeHandle(resetCheckedRowsRef, () => resetCheckedRows, [resetCheckedRows]);

    useImperativeHandle(
        ref,
        () => ({
            api: {
                refreshInfiniteCache: () => {
                    bimActsKC2OtherMakeRequest();
                    refreshWorkOrderOtherPriceList();
                },
                deleteExternally: (act: BimActKc2OtherItemData | undefined, onSuccessExternal: () => any) => {
                    setOnDeleteSuccessExternalCallback(() => onSuccessExternal);

                    handleOpenConfirmationDialog();
                    setActKc2OtherItemToDelete(act);
                },
                outerExpandOpener,
            },
            ...ref,
        }),
        [
            bimActsKC2OtherMakeRequest,
            handleOpenConfirmationDialog,
            outerExpandOpener,
            ref,
            refreshWorkOrderOtherPriceList,
        ],
    );

    const { deleteData, deleteStatus, deleteError, deleteSetData, handleDelete, onSuccess } =
        useBimWorkOrderDeletePrices({
            refreshTable: refreshWorkOrderOtherPriceList,
            resetCheckedIds: resetCheckedRows,
            workOrderId,
            checkedIds: checkedRowsArr,
            statusWorkOrder,
            workOrderObjectId: workOrderRootObjectId,
        });

    useEffect(
        () => () => {
            resetCheckedRows();
        },
        [resetCheckedRows, workOrderId, workOrderRootObjectId],
    );

    const { rowTotalsData } = useBimWorkOrderTotalsRow({
        chapter: BimChapterEnum.Others,
        constructionTypeId,
        workOrderId,
        workOrderObjectId: workOrderRootObjectId,
        priceLevel: undefined,
    });

    const fakeCheckedRowForVariables = useMemo(() => {
        if (totalDetailsOpened || totalDetailsRowOpenOuter) {
            return fakeRow;
        }

        return null;
    }, [totalDetailsOpened, totalDetailsRowOpenOuter]);

    const { rowTotalsData: rowTotalDetailsData, isLoading: totalDetailsLoading } = useBimWorkOrderTotalsRow({
        chapter: BimChapterEnum.Others,
        constructionTypeId,
        workOrderId,
        workOrderObjectId: workOrderRootObjectId,
        useInPrices: false,
        checkedPriceRowForDetails: fakeCheckedRowForVariables,
        priceLevel: undefined,
    });

    const refreshKey = useMemo(() => `${workOrderId}_${statusWorkOrder}`, [statusWorkOrder, workOrderId]);

    const { rules, rowClickHandler } = useHighLightRulesWithRequestData({
        requestIds: useMemo(() => ['bimUploadPriceFiles', 'addToBimWorkOrder'], []),
        dataKeyAccessor: 'itemIds',
        baseRules: getRowClass,
        gridApiRef: ref?.current?.current,
        refreshKey,
    });

    const handleRowClick = useCallback(
        (e: RowClickedEvent) => {
            rowClickHandler(e?.data?.id);
        },
        [rowClickHandler],
    );

    const isDisabledRadio = actKc2OtherForDetails?.status === BimActKc2OtherStatusEnum.Accepted;

    return (
        <>
            <TableTopPanel
                checkedRowsArr={checkedRowsArr}
                resetCheckedRows={resetCheckedRows}
                totalItems={totalItems}
                fetchedItems={fetchedItemsCounter}
                optionalColumns={settingsColumns}
                handleTableSettingsConfirm={handleTableSettingsConfirm}
                isColumnVisible={isOtherColumnVisible}
                areChildrenOnLeftSide
                showPinColumn
                tableApi={ref?.current}
            >
                <ButtonDeleteIds
                    deleteData={deleteData}
                    deleteStatus={deleteStatus}
                    deleteError={deleteError}
                    deleteSetData={deleteSetData}
                    handleDelete={handleDelete}
                    onSuccess={onSuccess}
                    style={{ marginRight: 10 }}
                />
            </TableTopPanel>

            <div className={`ag-theme-alpine BimWorkOrderPrice BimOtherTable ${Style.bimOtherTable} customHeaderColor`}>
                <AgGridTable
                    rowData={tableItems}
                    columnDefs={fullSetColumns}
                    animateRows
                    rowSelection="multiple"
                    suppressRowClickSelection
                    overlayNoRowsTemplate="Нет данных"
                    defaultColDef={defaultColDef}
                    rowClassRules={rules}
                    pinnedBottomRowData={rowTotalsData}
                    ref={ref}
                    getRowId={getRowId}
                    onRowClicked={handleRowClick}
                    icons={AG_GRID_ICON_SETUP}
                    suppressDragLeaveHidesColumns
                    tooltipShowDelay={200}
                    context={{
                        tableUniqueId: `others_${workOrderRootObjectId}`,
                    }}
                />
            </div>

            {(totalDetailsRowOpenOuter || totalDetailsOpened) && (
                <BimWorkOrderPriceRowDetails
                    modalTitle="Итоги прочих затрат"
                    data={rowTotalDetailsData}
                    columns={detailsColumns}
                    onClose={closeTotalDetails}
                    isDetailsLoading={totalDetailsLoading}
                    defaultColDef={defaultColDef}
                    styleClass="BimWorkOrderOtherPriceDetails customHeaderColor"
                    priceLevel={undefined}
                />
            )}

            {!!actKc2OtherForDetails && (
                <BimWorkOrderOtherActKc2Details
                    actKC2={actKc2OtherForDetails}
                    onClose={onActKc2OtherDetailsClose}
                    defaultColDef={defaultColDef}
                    styleClass="ActKC2Details"
                    isActionDisabled={isDisabledRadio}
                />
            )}

            <DeleteItemFrom
                modalTextHeader="Удаление прочих затрат"
                modalTextConfirmation="Вы уверены, что хотите удалить записи?"
                isOpened={isSingleDeleteModalOpened}
                closeDeleteModal={closeSingleDeleteModalHandler}
                deleteData={deleteData}
                deleteStatus={deleteStatus}
                deleteError={deleteError}
                deleteSetData={deleteSetData}
                handleDelete={handleDelete}
                handleSuccess={() => undefined}
            />

            <ConfirmationDialog
                textHeader="Удаление Акта КС-2"
                textConfirmation="Вы уверены, что хотите удалить Акт?"
                cancelButtonText="Не удалять"
                confirmButtonText="Удалить"
                isOpened={isOpenConfirmationDialog && !!actKc2OtherItemToDelete}
                onSubmit={handleActionQuery}
                onClose={onDeleteActConfirmationClose}
            />

            <ConfirmationDialog
                textHeader="Создание корректировочного акта"
                textConfirmation={
                    <div style={{ display: 'flex', flexDirection: 'column', padding: '0 24px' }}>
                        <div style={{ marginBottom: '16px' }}>Вы уверены, что хотите создать корректировочный Акт?</div>
                        <div>
                            После создания корректировочного Акта текущий Акт КС-2 не будет участвовать в расчете
                            Накопительной по НЗ и Договора.
                        </div>
                    </div>
                }
                cancelButtonText="Отмена"
                confirmButtonText="Создать"
                isOpened={isOpenConfirmationDialog && !!actKc2OtherForCorrection}
                onSubmit={handleActionQuery}
                onClose={onCorrectActConfirmationClose}
            />
        </>
    );
});

BimWorkOrderOthersTable.displayName = 'BimWorkOrderOthersTable';
